import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageSquare, Sparkles } from "lucide-react";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";
import { AGENT_VISUALS } from "@/lib/agents";

export default function AgentProfile() {
  const { code } = useParams();
  const [agent, setAgent] = useState(null);
  const navigate = useNavigate();
  const v = AGENT_VISUALS[code];

  useEffect(() => {
    api.get("/agents").then((r) => {
      const found = r.data.active.find((a) => a.code === code);
      setAgent(found);
    });
  }, [code]);

  if (!agent || !v) {
    return (
      <ConsoleShell>
        <div className="max-w-3xl mx-auto py-24 px-6 text-center text-zinc-500">Chargement…</div>
      </ConsoleShell>
    );
  }

  return (
    <ConsoleShell>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/app" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition mb-8" data-testid="agent-profile-back">
          <ArrowLeft className="w-4 h-4" /> Retour aux agents
        </Link>

        <div className="grid md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-4 flex flex-col items-center md:items-start">
            <div className="w-40 h-40 rounded-full overflow-hidden mb-5" style={{ boxShadow: `0 0 0 3px ${agent.color}` }}>
              <img src={v.avatar} alt={agent.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" />
              <span className="text-xs text-emerald-400 uppercase tracking-[0.2em]">Online</span>
            </div>
            <h1 className="font-display text-5xl tracking-tighter font-semibold" data-testid="agent-profile-name">{agent.name}</h1>
            <div className="text-zinc-400 mb-2">{agent.role}</div>
            <div className="text-xs font-mono text-zinc-600">{agent.model}</div>
          </div>

          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">À propos</div>
            <p className="text-xl text-zinc-200 leading-relaxed mb-8">{v.description}</p>

            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Compétences</div>
            <div className="flex flex-wrap gap-2 mb-10">
              {v.skills.map((s) => (
                <span
                  key={s}
                  className="px-3 py-1.5 border border-white/15 text-sm"
                  style={{ borderColor: agent.color + "44", background: agent.color + "10" }}
                >
                  {s}
                </span>
              ))}
            </div>

            <button
              onClick={() => navigate(`/app/chat/${code}`)}
              data-testid="agent-profile-start-chat"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-black hover:bg-zinc-200 transition font-medium"
            >
              <MessageSquare className="w-4 h-4" />
              Démarrer une conversation
            </button>
          </div>
        </div>

        <div className="bg-[#141414] border border-white/10 p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Exemples de questions
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {v.examples.map((q, i) => (
              <button
                key={i}
                onClick={() => navigate(`/app/chat/${code}`, { state: { prefill: q } })}
                data-testid={`example-${code}-${i}`}
                className="text-left p-4 border border-white/10 hover:border-white/30 hover:bg-white/5 transition text-sm group"
              >
                "{q}"
                <div className="mt-2 text-xs text-zinc-500 group-hover:text-white flex items-center gap-1">
                  Essayer <ArrowRight className="w-3 h-3" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </ConsoleShell>
  );
}
